import { t } from "i18next";
import moment from "moment";

import { SmartlistType } from "../../api/MediaAPI.types";
import {
  hasCoverPicture,
  slugIsValid,
  transformSlugBeforeUpdate,
} from "../Translations";

import type {
  GetSmartlistResponse,
  SmartlistSeason,
  SmartlistSeasonToCreate,
  SmartlistSeasonUpdated,
  SmartlistSerieToCreate,
} from "../../api/SmartlistAPI.types";

const serieOrSeasonIsValid = (
  serieOrSeason:
    | GetSmartlistResponse
    | SmartlistSeason
    | SmartlistSerieToCreate
    | SmartlistSeasonUpdated,
): Set<string> => {
  const errorMessages = new Set<string>();

  if (!serieOrSeason.label || serieOrSeason.label.length <= 0) {
    errorMessages.add(t("editor-errors:noLabel"));
  }

  serieOrSeason.translations?.forEach((translation) => {
    if (translation.default) {
      if (
        (!translation.name || translation.name.length <= 0) &&
        !errorMessages.has(t("editor-errors:noDefaultTitle"))
      ) {
        errorMessages.add(t("editor-errors:noDefaultTitle"));
      }

      if (
        !hasCoverPicture(translation) &&
        !errorMessages.has(t("editor-errors:noDefaultPicture"))
      ) {
        errorMessages.add(t("editor-errors:noDefaultPicture"));
      }
    }
  });

  if (
    serieOrSeason.end_date &&
    moment(serieOrSeason.end_date).isBefore(moment(serieOrSeason.begin_date))
  ) {
    errorMessages.add(t("editor-errors:endBeforeStartDate"));
  }

  return errorMessages;
};

export const serieHasDefaultName = (serie: GetSmartlistResponse): boolean => {
  let pass = true;

  serie.translations?.forEach((translation) => {
    if (
      (translation.default && !translation.name) ||
      translation.name.length <= 0
    ) {
      pass = false;
    }
  });

  return pass;
};

export const genereateNewSerie = ({
  defaultLanguage,
  defaultLanguageUuid,
}: {
  defaultLanguage: string;
  defaultLanguageUuid: string;
}): SmartlistSerieToCreate => ({
  begin_date: moment().format(),
  countries: [],
  enabled: true,
  end_date: null,
  geoblock_mode: "disabled",
  label: t("editor-series:newSerie"),
  smartlist_items: [],
  temporal_restriction: false,
  translations: [
    {
      default: true,
      short_description: "",
      description: "",
      language: defaultLanguage,
      language_uuid: defaultLanguageUuid,
      name: "",
    },
  ],
  translations_indexes: {
    language_uuid: {
      [defaultLanguageUuid]: [0],
    },
    default: { true: [0] },
  },
  type: SmartlistType.SERIES,
});

export const generateNewSeason = (
  serie: SmartlistSerieToCreate,
  rank: number,
): SmartlistSeasonToCreate => ({
  begin_date: moment().format(),
  countries: [],
  end_date: null,
  geoblock_mode: "disabled",
  temporal_restriction: false,
  enabled: true,
  label: "SmartlistSeason",
  name: "SmartlistSeason",
  smartlist_items: [],
  translations: serie.translations.map((it) => ({
    language: it.language,
    language_uuid: it.language_uuid,
    default: it.default,
    name: "",
    description: "",
    slug: "",
  })),
  translations_indexes: {
    ...serie.translations_indexes,
    default: {
      true: [serie.translations_indexes.default.true[0] as number],
    },
  },
  rank,
  type: SmartlistType.SEASON,
});

const checkForDuplicateSlugs = (
  seasons: (SmartlistSeason | SmartlistSeasonUpdated)[],
): Set<string> => {
  const errorMessages = new Set<string>();
  const seenSlugs = new Set<string>();

  seasons.forEach((season) => {
    season.translations.forEach((translation) => {
      if (translation.slug?.trim()) {
        const slugValue = translation.slug.trim();
        if (seenSlugs.has(slugValue)) {
          errorMessages.add(
            t("editor-errors:duplicateSlug", {
              slug: slugValue,
            }),
          );
        } else {
          seenSlugs.add(slugValue);
        }
      }
    });
  });
  return errorMessages;
};

export const getSerieAndSeasonsErrors = (
  serie: GetSmartlistResponse | SmartlistSerieToCreate,
  seasons: (SmartlistSeason | SmartlistSeasonUpdated)[],
): Set<string> => {
  const errorMessages = new Set<string>();

  if (seasons?.length <= 0) {
    errorMessages.add(t("editor-errors:noSeasons"));
  }

  checkForDuplicateSlugs(seasons).forEach((error) => errorMessages.add(error));

  const smartlists = [serie, ...seasons];

  smartlists.forEach((item) => {
    serieOrSeasonIsValid(item).forEach((error) => errorMessages.add(error));
  });

  return errorMessages;
};

export const convertSerieAndSeasonsToValidSlug = (
  serie: GetSmartlistResponse,
  seasons: (SmartlistSeason | SmartlistSeasonUpdated)[],
): {
  serie: GetSmartlistResponse;
  seasons: (SmartlistSeason | SmartlistSeasonUpdated)[];
} => {
  const updatedSerieTranslations = serie.translations.map((translation) => {
    const isValidSlug = slugIsValid(translation.slug);
    return {
      ...translation,
      slug: isValidSlug
        ? translation.slug
        : (transformSlugBeforeUpdate(translation.slug) ?? ""),
    };
  });

  const updatedSeasons = seasons.map((season) => {
    const updatedSeasonTranslations = season.translations.map((translation) => {
      const isValidSlug = slugIsValid(translation.slug);
      return {
        ...translation,
        slug: isValidSlug
          ? translation.slug
          : (transformSlugBeforeUpdate(translation.slug) ?? ""),
      };
    });
    return {
      ...season,
      translations: updatedSeasonTranslations,
    };
  });

  return {
    serie: { ...serie, translations: updatedSerieTranslations },
    seasons: updatedSeasons,
  };
};
